import { OverflowList } from '@theme/overflow-list';
import VariantPicker from '@theme/variant-picker';
import { ProductComponent } from '@theme/view-event-elements';
import { debounce, isDesktopBreakpoint, mediaQueryLarge, yieldToMainThread } from '@theme/utilities';
import { SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

const STOREFRONT_API_ACCESS_TOKEN = '532f2372c93df999c5aa13c5e26098b2';
const STOREFRONT_API_VERSION = '2026-07';
const siblingProductsCache = new Map();

/**
 * @typedef {object} ProductCardLinkRefs
 * @property {HTMLElement} [cardGallery] - The card gallery element.
 * @property {HTMLImageElement[]} [imagesToTransition] - The images to transition.
 */

/**
 * A custom element for product links with images for transitions to PDP.
 * This is a base class that is extended by ProductCard.
 * Used directly by resource-card.liquid for non-product-card scenarios.
 * Extends ProductComponent to automatically emit product:view events when visible.
 *
 * @template {ProductCardLinkRefs} [T=ProductCardLinkRefs]
 * @extends {ProductComponent<T>}
 */
export class ProductCardLink extends ProductComponent {
  get productTransitionEnabled() {
    return this.getAttribute('data-product-transition') === 'true';
  }

  get featuredMediaUrl() {
    return this.getAttribute('data-featured-media-url');
  }

  /**
   * Handles the click event for view transitions.
   * @param {Event} event
   */
  handleViewTransition(event) {
    // If the event has been prevented, don't do anything, another component is handling the click
    if (event.defaultPrevented) return;

    // If the event was on an interactive element, don't do anything, this is not a navigation
    if (event.target instanceof Element) {
      const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');
      if (interactiveElement) return;
    }

    if (!this.productTransitionEnabled) return;

    const { cardGallery } = this.refs;
    if (!cardGallery || !cardGallery.hasAttribute('data-view-transition-to-main-product')) return;

    // Check on the current active image, whether it's a product card image or a resource card image
    const { imagesToTransition } = this.refs;
    const activeImage =
      imagesToTransition?.find(
        (/** @type {HTMLImageElement} */ image) =>
          image.closest('slideshow-slide')?.getAttribute('aria-hidden') === 'false'
      ) || imagesToTransition?.[imagesToTransition.length - 1];

    if (activeImage instanceof HTMLImageElement) this.#setImageSrcset(activeImage);

    cardGallery.setAttribute('data-view-transition-type', 'product-image-transition');
    cardGallery.setAttribute('data-view-transition-triggered', 'true');
  }

  /**
   * Sets the srcset for the image
   * @param {HTMLImageElement} image
   */
  #setImageSrcset(image) {
    if (!this.featuredMediaUrl) return;

    const currentImageUrl = new URL(image.currentSrc);

    // Deliberately not using origin, as it includes the protocol, which is usually skipped for featured media
    const currentImageRawUrl = currentImageUrl.host + currentImageUrl.pathname;

    if (!this.featuredMediaUrl.includes(currentImageRawUrl)) {
      const imageFade = image.animate([{ opacity: 0.8 }, { opacity: 1 }], {
        duration: 125,
        easing: 'ease-in-out',
      });

      imageFade.onfinish = () => {
        image.srcset = this.featuredMediaUrl ?? '';
      };
    }
  }
}

if (!customElements.get('product-card-link')) {
  customElements.define('product-card-link', ProductCardLink);
}

/**
 * A custom element that displays a product card.
 * Extends ProductCardLink to inherit view transition functionality.
 *
 * @typedef {object} ProductCardRefs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 * @property {HTMLImageElement[]} [imagesToTransition] - The images to transition.
 * @extends {ProductCardLink<ProductCardRefs>}
 */
export class ProductCard extends ProductCardLink {
  requiredRefs = ['productCardLink'];

  get productPageUrl() {
    const link = this.refs.productCardLink;
    if (!link.getAttribute('href')) return '';

    const url = new URL(link.href);
    const variantOverride = this.#getProductPageVariantOverride();

    if (variantOverride?.action === 'set') {
      url.searchParams.set('variant', variantOverride.variantId);
    } else if (variantOverride?.action === 'remove') {
      url.searchParams.delete('variant');
    }

    return url.toString();
  }

  /**
   * Gets the user-selected variant ID that still needs to be synced from product-card state.
   * @returns {string | null} Variant ID or null.
   */
  getSelectedVariantId() {
    const variantOverride = this.#getProductPageVariantOverride();
    return variantOverride?.action === 'set' ? variantOverride.variantId : null;
  }

  /**
   * Gets the user-selected variant override for product-page URLs.
   * @returns {{ action: 'set', variantId: string } | { action: 'remove' } | null}
   */
  #getProductPageVariantOverride() {
    const link = this.refs.productCardLink;
    if (link.getAttribute('href')) {
      const url = new URL(link.href);
      if (url.searchParams.has('variant')) return null;
    }

    const variantId = this.#getCheckedVariantId();
    if (variantId === undefined) return null;

    return variantId ? { action: 'set', variantId } : { action: 'remove' };
  }

  /** @returns {string | undefined} */
  #getCheckedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return checkedInput?.dataset.variantId;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  #fetchProductPageHandler = () => {
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
  };

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');

    this.#handleQuickAdd();

    this.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);

    this.addEventListener('click', this.navigateToProduct);

    // Preload the next image on the slideshow to avoid white flashes on previewImage
    setTimeout(() => {
      if (this.refs.slideshow?.isNested) {
        this.#preloadNextPreviewImage();
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.navigateToProduct);
  }

  #preloadNextPreviewImage() {
    const currentSlide = this.refs.slideshow?.slides?.[this.refs.slideshow?.current];
    currentSlide?.nextElementSibling?.querySelector('img[loading="lazy"]')?.removeAttribute('loading');
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  /**
   * Handles the product select event (variant selected and updated).
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    // Update variant picker when variant:selected event fires
    const { optionValueId, variantId, connectedProductUrl } = event.detail ?? {};
    if (optionValueId && event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(optionValueId);
    }

    // Empty string removes ?variant=.
    if (typeof variantId === 'string') {
      this.applyVariantToLinks(variantId, typeof connectedProductUrl === 'string' ? connectedProductUrl : undefined);
    }

    // Wait for variant:update data via promise
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;

        // Update price, availability, and URL based on new variant
        this.updatePrice(html);
        this.#isUnavailableVariantSelected(html);
        this.#updateProductUrl(html, typeof variantId === 'string' ? variantId : undefined);
        this.refs.quickAdd?.fetchProductPage(this.productPageUrl);

        if (event.target !== this.variantPicker) {
          this.variantPicker?.updateVariantPicker(html);
        }

        this.#updateVariantImages();
        this.#previousSlideIndex = null;

        // Remove attribute after re-rendering since a variant selection has been made
        this.removeAttribute('data-no-swatch-selected');

        // Force overflow list to reflow after variant update
        this.#updateOverflowList();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-card] Event promise rejected:', error);
      });
  };

  /**
   * Forces the overflow list to recalculate by dispatching a reflow event.
   * This ensures the overflow counter displays correctly after variant updates.
   */
  #updateOverflowList() {
    // Find the overflow list in the variant picker
    const overflowList = this.querySelector('swatches-variant-picker-component overflow-list');
    const isActiveOverflowList = overflowList?.querySelector('[slot="overflow"]') ? true : false;
    if (!overflowList || !isActiveOverflowList) return;

    // Use requestAnimationFrame to ensure DOM has been updated
    requestAnimationFrame(() => {
      // Dispatch a reflow event to trigger recalculation
      overflowList.dispatchEvent(
        new CustomEvent('reflow', {
          bubbles: true,
          detail: {},
        })
      );
    });
  }

  /**
   * Updates the DOM with a new price.
   * @param {Document} html - The parsed HTML document with updated variant data.
   */
  updatePrice(html) {
    const priceContainer = this.querySelectorAll(`product-price [ref='priceContainer']`)[1];
    const newPriceElement = html.querySelector(`product-price [ref='priceContainer']`);

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * Updates the product URL based on the variant update.
   * @param {Document} html - The parsed HTML document with updated variant data.
   * @param {string} [intendedVariantId] - User-intended variant.
   */
  #updateProductUrl(html, intendedVariantId) {
    const responseProductCard = html.querySelector('product-card');
    const anchorElement = responseProductCard?.querySelector('a');
    const featuredMediaUrl = responseProductCard?.getAttribute('data-featured-media-url');

    // Update the featured media URL for view transitions (inherited from ProductCardLink)
    if (featuredMediaUrl) {
      this.setAttribute('data-featured-media-url', featuredMediaUrl);
    }

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      const responseUrl = new URL(anchorElement.href);
      this.#updateLinks({
        pathname: responseUrl.pathname,
        variantId: intendedVariantId ?? responseUrl.searchParams.get('variant'),
      });
    }
  }

  /**
   * Applies the given variant ID to the variant param of every link in the card.
   * Public so the components, namely swatches component can call it.
   * @param {string | null} variantId - The variant ID to set, or null to remove it.
   * @param {string} [productUrl] - Product URL path to adopt.
   */
  applyVariantToLinks(variantId, productUrl) {
    let pathname;
    if (productUrl) {
      try {
        pathname = new URL(productUrl, window.location.origin).pathname;
      } catch (error) {
        console.warn('[product-card] Invalid product URL:', productUrl, error);
        return;
      }
    }

    this.#updateLinks({ pathname, variantId });
  }

  /**
   * Updates every link in the card (overlay, gallery, and title) in place, preserving
   * each link's existing query params so tracking params stay in sync.
   * Always sets (or removes) the `variant` param; optionally adopts a new pathname (for combined-listing child navigation).
   * @param {object} options
   * @param {string} [options.pathname] - New pathname to adopt (combined-listing child navigation). Omit to keep each link's current path.
   * @param {string | null} [options.variantId] - Variant ID to set, or null/undefined to remove it.
   */
  #updateLinks({ pathname, variantId }) {
    const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;
    for (const linkEl of [productCardLink, cardGalleryLink, productTitleLink]) {
      if (!(linkEl instanceof HTMLAnchorElement) || !linkEl.href) continue;
      const url = new URL(linkEl.href);
      if (pathname) url.pathname = pathname;
      if (variantId) url.searchParams.set('variant', variantId);
      else url.searchParams.delete('variant');
      linkEl.href = url.toString();
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {Document} html - The parsed HTML document with updated variant data.
   */
  #isUnavailableVariantSelected(html) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (html.querySelectorAll('input:checked'));

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Hide the variant images that are not for the selected variant.
   */
  #updateVariantImages() {
    const { slideshow } = this.refs;
    if (!this.variantPicker?.selectedOption) {
      return;
    }

    const selectedImageId = this.variantPicker?.selectedOption.dataset.optionMediaId;

    if (slideshow && selectedImageId) {
      const { slides = [] } = slideshow.refs;

      for (const slide of slides) {
        if (slide.getAttribute('variant-image') == null) continue;

        slide.hidden = slide.getAttribute('slide-id') !== selectedImageId;
      }

      slideshow.select({ id: selectedImageId }, undefined, { animate: false });
    }
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }
  /** @type {number | null} */
  #previousSlideIndex = null;

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) {
      this.#previousSlideIndex = event.detail.index;
    }
  };

  /**
   * Previews a variant.
   * @param {string} id - The id of the variant to preview.
   */
  previewVariant(id) {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();
    slideshow.select({ id }, undefined, { animate: false });
  }

  /**
   * Previews the next image.
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();

    if (this.#previousSlideIndex != null && this.#previousSlideIndex > 0) {
      slideshow.select(this.#previousSlideIndex, undefined, { animate: false });
    } else {
      slideshow.next(undefined, { animate: false });
      setTimeout(() => this.#preloadNextPreviewImage());
    }
  }

  /**
   * Resets the image to the variant image.
   * @param {PointerEvent} event - The pointer event.
   */
  resetImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!this.variantPicker) {
      if (!slideshow) return;
      slideshow.previous(undefined, { animate: false });
    } else {
      this.#resetVariant();
    }
  }

  /**
   * Resets the image to the variant image.
   */
  #resetVariant = () => {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    // If we have a selected variant, always use its image
    if (this.variantPicker?.selectedOption) {
      const id = this.variantPicker.selectedOption.dataset.optionMediaId;
      if (id) {
        slideshow.select({ id }, undefined, { animate: false });
        return;
      }
    }

    // No variant selected - use initial slide if it's valid
    const initialSlide = slideshow.initialSlide;
    const slideId = initialSlide?.getAttribute('slide-id');
    if (initialSlide && slideshow.slides?.includes(initialSlide) && slideId) {
      slideshow.select({ id: slideId }, undefined, { animate: false });
      return;
    }

    // No valid initial slide or selected variant - go to previous
    slideshow.previous(undefined, { animate: false });
  };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute('data-no-navigation')) return;

    const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');

    // If the click was on an interactive element, do nothing.
    if (interactiveElement) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const infiniteResultsList = this.closest('results-list[infinite-scroll="true"]');
    if (!window.Shopify.designMode && infiniteResultsList) {
      const url = new URL(window.location.href);
      const parent = this.closest('li');
      url.hash = productCardAnchor;
      if (parent && parent.dataset.page) {
        url.searchParams.set('page', parent.dataset.page);
      }

      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    const targetLink = event.target.closest('a');
    // Let the native navigation handle the click if it was on a link.
    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };

  /**
   * Resets the variant.
   */
  resetVariant = debounce(this.#resetVariant, 100);
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * Displays title-derived sibling products and previews their data without changing card state.
 */
class SiblingColourSwatches extends HTMLElement {
  #imageSnapshot = null;
  #priceSnapshot = null;
  #titleSnapshot = null;

  connectedCallback() {
    const title = this.dataset.productTitle;
    const separatorIndex = title?.indexOf(' - ') ?? -1;
    if (separatorIndex < 1 || !STOREFRONT_API_ACCESS_TOKEN) return;

    const baseTitle = title.slice(0, separatorIndex).trim();
    this.#loadSiblings(baseTitle);
  }

  disconnectedCallback() {
    this.#restorePreview();
  }

  /**
   * Fetches title candidates once per page; the API search is intentionally followed by an
   * exact client-side comparison because Shopify search can return partial title matches.
   * @param {string} baseTitle
   */
  async #loadSiblings(baseTitle) {
    try {
      const siblings = await getSiblingProducts(baseTitle);
      if (siblings.length < 2) return;

      this.#render(siblings);
    } catch (error) {
      console.warn('[sibling-colour-swatches] Unable to load sibling products:', error);
    }
  }

  /** @param {SiblingProduct[]} siblings */
  #render(siblings) {
    const currentHandle = this.dataset.currentProductHandle;
    const fragment = document.createDocumentFragment();

    siblings
      .sort((a, b) => {
        if (a.handle === currentHandle) return -1;
        if (b.handle === currentHandle) return 1;
        return a.colour.localeCompare(b.colour);
      })
      .forEach((sibling) => {
        const link = document.createElement('a');
        link.className = 'sibling-colour-swatch';
        link.href = sibling.url;
        link.setAttribute('aria-label', `View ${sibling.colour}`);
        if (sibling.handle === currentHandle) link.setAttribute('aria-current', 'page');

        const chip = document.createElement('span');
        chip.className = 'sibling-colour-swatch__chip';
        const fallbackColour = this.#fallbackColour(sibling.colour);
        if (fallbackColour) chip.style.setProperty('--sibling-swatch-background', fallbackColour);

        const image = document.createElement('img');
        image.className = 'sibling-colour-swatch__image';
        image.src = getSwatchFileUrl(sibling.colour);
        image.alt = '';
        image.addEventListener('error', () => image.remove(), { once: true });
        chip.append(image);
        link.append(chip);

        link.addEventListener('pointerenter', () => this.#preview(sibling));
        link.addEventListener('pointerleave', () => this.#restorePreview());
        link.addEventListener('focus', () => this.#preview(sibling));
        link.addEventListener('blur', () => this.#restorePreview());
        link.addEventListener('click', (event) => this.#selectSibling(event, sibling));
        fragment.append(link);
      });

    // Keep chip links out of the card's click delegation while preserving native link behaviour.
    this.addEventListener('click', (event) => event.stopPropagation());
    // Facet filtering can reconnect an existing card, so replace rather than append its links.
    this.replaceChildren(fragment);
  }

  /** @param {string} colour */
  #fallbackColour(colour) {
    const normalisedColour = colour.toLowerCase();
    if (normalisedColour.includes('rose gold')) return this.#settingColour('--sibling-swatch-rose-gold');
    if (normalisedColour.includes('gold')) return this.#settingColour('--sibling-swatch-gold');
    if (normalisedColour.includes('silver')) return this.#settingColour('--sibling-swatch-silver');

    return CSS.supports('color', colour) ? colour : '';
  }

  /** @param {string} property */
  #settingColour(property) {
    return getComputedStyle(this).getPropertyValue(property).trim();
  }

  /** @param {SiblingProduct} sibling */
  #preview(sibling) {
    const card = this.closest('product-card');
    if (!card) return;

    const titleBlock = card.querySelector("[ref='productTitleLink'] .text-block");
    const title = titleBlock?.querySelector('h1, h2, h3, h4, h5, h6, p') || titleBlock?.firstElementChild;
    if (title) {
      if (!this.#titleSnapshot) this.#titleSnapshot = { title, html: title.innerHTML };
      title.textContent = sibling.title;
    }

    const image = getActiveCardImage(card);
    if (image) {
      if (!this.#imageSnapshot) {
        this.#imageSnapshot = {
          image,
          src: image.getAttribute('src'),
          srcset: image.getAttribute('srcset'),
          sizes: image.getAttribute('sizes'),
        };
      }
      image.src = sibling.imageUrl;
      image.srcset = sibling.imageUrl;
      image.removeAttribute('sizes');
    }

    const priceContainers = card.querySelectorAll("product-price [ref='priceContainer']");
    if (!this.#priceSnapshot) {
      this.#priceSnapshot = [...priceContainers].map((container) => ({ container, html: container.innerHTML }));
    }
    for (const container of priceContainers) {
      container.innerHTML = renderSiblingPrice(sibling);
    }
  }

  #restorePreview() {
    if (this.#imageSnapshot) {
      const { image, src, srcset, sizes } = this.#imageSnapshot;
      restoreAttribute(image, 'src', src);
      restoreAttribute(image, 'srcset', srcset);
      restoreAttribute(image, 'sizes', sizes);
      this.#imageSnapshot = null;
    }

    if (this.#priceSnapshot) {
      for (const { container, html } of this.#priceSnapshot) container.innerHTML = html;
      this.#priceSnapshot = null;
    }

    if (this.#titleSnapshot) {
      const { title, html } = this.#titleSnapshot;
      title.innerHTML = html;
      this.#titleSnapshot = null;
    }
  }

  /**
   * Commits a sibling to the current card instead of navigating away. Modifier clicks
   * deliberately retain their normal link behaviour so customers can still open a PDP in a new tab.
   * @param {MouseEvent} event
   * @param {SiblingProduct} sibling
   */
  #selectSibling(event, sibling) {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) return;

    event.preventDefault();
    event.stopPropagation();
    this.#restorePreview();

    const card = this.closest('product-card');
    if (!card) return;

    card.dataset.productId = sibling.id;
    card.setAttribute('data-featured-media-url', sibling.imageUrl);

    const links = card.querySelectorAll('a[ref="productCardLink"], a[ref="productTitleLink"], a[ref="cardGalleryLink"]');
    for (const link of links) {
      link.href = sibling.url;
      link.setAttribute('aria-label', sibling.title);
    }
    if (typeof card.applyVariantToLinks === 'function') {
      card.applyVariantToLinks(sibling.variantId, sibling.url);
    }

    const titleBlock = card.querySelector("[ref='productTitleLink'] .text-block");
    const title = titleBlock?.querySelector('h1, h2, h3, h4, h5, h6, p') || titleBlock?.firstElementChild;
    if (title) title.textContent = sibling.title;

    // The card gallery has already reserved its image slots; reuse them so the
    // slideshow keeps its controls, sizing and image-loading behaviour intact.
    const images = card.querySelectorAll('.card-gallery .product-media__image');
    const imageUrls = sibling.imageUrls.length ? sibling.imageUrls : [sibling.imageUrl];
    images.forEach((image, index) => {
      const imageUrl = imageUrls[index % imageUrls.length];
      image.src = imageUrl;
      image.srcset = imageUrl;
      image.removeAttribute('sizes');
    });

    const priceContainers = card.querySelectorAll("product-price [ref='priceContainer']");
    for (const container of priceContainers) container.innerHTML = renderSiblingPrice(sibling);

    const quickAdd = card.querySelector('quick-add-component');
    if (quickAdd instanceof HTMLElement) {
      quickAdd.dataset.productId = sibling.id;
      quickAdd.dataset.productTitle = sibling.title;

      const productForm = quickAdd.querySelector('product-form-component');
      if (productForm instanceof HTMLElement) productForm.dataset.productId = sibling.id;

      const variantInput = quickAdd.querySelector("input[ref='variantId']");
      if (variantInput instanceof HTMLInputElement) {
        variantInput.value = sibling.variantId;
        variantInput.disabled = !sibling.availableForSale;
      }

      const addButton = quickAdd.querySelector('.quick-add__button--add');
      if (addButton instanceof HTMLButtonElement) addButton.disabled = !sibling.availableForSale;
    }

    for (const swatch of this.querySelectorAll('.sibling-colour-swatch')) {
      if (swatch.getAttribute('href') === sibling.url) swatch.setAttribute('aria-current', 'page');
      else swatch.removeAttribute('aria-current');
    }
    this.dataset.currentProductHandle = sibling.handle;
  }
}

if (!customElements.get('sibling-colour-swatches')) {
  customElements.define('sibling-colour-swatches', SiblingColourSwatches);
}

/**
 * @typedef {{ id: string, title: string, handle: string, url: string, colour: string, imageUrl: string, imageUrls: string[], variantId: string, availableForSale: boolean, price: { amount: string, currencyCode: string }, compareAtPrice: { amount: string, currencyCode: string } | null }} SiblingProduct
 */

/** @param {string} baseTitle @returns {Promise<SiblingProduct[]>} */
function getSiblingProducts(baseTitle) {
  const cacheKey = normaliseTitle(baseTitle);
  if (!siblingProductsCache.has(cacheKey)) {
    siblingProductsCache.set(cacheKey, fetchSiblingProducts(baseTitle));
  }
  return siblingProductsCache.get(cacheKey);
}

/** @param {string} baseTitle @returns {Promise<SiblingProduct[]>} */
async function fetchSiblingProducts(baseTitle) {
  const response = await fetch(`/api/${STOREFRONT_API_VERSION}/graphql.json`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': STOREFRONT_API_ACCESS_TOKEN,
    },
    body: JSON.stringify({
      query: `
        query SiblingProducts($query: String!) {
          products(first: 100, query: $query) {
            nodes {
              id
              title
              handle
              onlineStoreUrl
              featuredImage { url }
              images(first: 10) { nodes { url } }
              variants(first: 100) {
                nodes {
                  id
                  availableForSale
                  price { amount currencyCode }
                  compareAtPrice { amount currencyCode }
                }
              }
            }
          }
        }
      `,
      variables: { query: `title:\"${baseTitle.replaceAll('\\', '\\\\').replaceAll('"', '\\"')}\"` },
    }),
  });

  if (!response.ok) throw new Error(`Storefront API returned ${response.status}`);
  const payload = await response.json();
  if (payload.errors?.length) throw new Error(payload.errors.map((error) => error.message).join(', '));

  return payload.data.products.nodes.reduce((siblings, product) => {
    const { base, colour } = splitProductTitle(product.title);
    if (normaliseTitle(base) !== normaliseTitle(baseTitle) || !colour || !product.featuredImage?.url) return siblings;

    const variant = product.variants.nodes.find((item) => item.availableForSale) || product.variants.nodes[0];
    if (!variant) return siblings;

    siblings.push({
      id: getLegacyId(product.id),
      title: product.title,
      handle: product.handle,
      url: product.onlineStoreUrl || `/products/${product.handle}`,
      colour,
      imageUrl: product.featuredImage.url,
      imageUrls: product.images.nodes.map((image) => image.url),
      variantId: getLegacyId(variant.id),
      availableForSale: variant.availableForSale,
      price: variant.price,
      compareAtPrice: variant.compareAtPrice,
    });
    return siblings;
  }, []);
}

/**
 * Storefront API IDs are global IDs while the theme's card and Ajax-cart data
 * use Shopify's numeric resource IDs.
 * @param {string} id
 */
function getLegacyId(id) {
  return id.split('/').pop() || id;
}

/** @param {string} title */
function splitProductTitle(title) {
  const separatorIndex = title.indexOf(' - ');
  return separatorIndex < 1
    ? { base: title, colour: '' }
    : { base: title.slice(0, separatorIndex).trim(), colour: title.slice(separatorIndex + 3).trim() };
}

/** @param {string} title */
function normaliseTitle(title) {
  return title.trim().toLocaleLowerCase();
}

/** @param {string} colour */
function getSwatchFileUrl(colour) {
  const filename = colour
    .trim()
    .toLocaleLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  return `/cdn/shop/files/colour-${filename}.png`;
}

/** @param {HTMLElement} card */
function getActiveCardImage(card) {
  return (
    card.querySelector("slideshow-slide[aria-hidden='false'] .product-media__image") ||
    card.querySelector('.card-gallery .product-media__image')
  );
}

/** @param {SiblingProduct} sibling */
function renderSiblingPrice(sibling) {
  const price = formatMoney(sibling.price);
  const compareAtPrice = sibling.compareAtPrice && Number(sibling.compareAtPrice.amount) > Number(sibling.price.amount)
    ? formatMoney(sibling.compareAtPrice)
    : null;

  return compareAtPrice
    ? `<span class="price-item__group price">${price}</span><span class="price-item__group price-item--regular compare-at-price">${compareAtPrice}</span>`
    : `<span class="price">${price}</span>`;
}

/** @param {{ amount: string, currencyCode: string }} money */
function formatMoney(money) {
  return new Intl.NumberFormat(document.documentElement.lang || undefined, {
    style: 'currency',
    currency: money.currencyCode,
  }).format(Number(money.amount));
}

/** @param {Element} element @param {string} name @param {string | null} value */
function restoreAttribute(element, name, value) {
  if (value == null) element.removeAttribute(name);
  else element.setAttribute(name, value);
}

/**
 * A custom element that displays a variant picker with swatches.
 * @typedef {import('@theme/variant-picker').VariantPickerRefs & {overflowList: HTMLElement}} SwatchesRefs
 */

/**
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  /**
   * Handles card swatch changes.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Check if this is a swatch input
    const isSwatchInput = event.target instanceof HTMLInputElement && event.target.name?.includes('-swatch');
    const clickedSwatch = event.target;
    const hasAvailableVariant = clickedSwatch.dataset.hasAvailableVariant === 'true';
    const firstAvailableVariantId = clickedSwatch.dataset.firstAvailableOrFirstVariantId;

    // Request the first available variant for this swatch.
    if (isSwatchInput && hasAvailableVariant && firstAvailableVariantId) {
      event.stopPropagation();
      this.updateSelectedOption(clickedSwatch);

      const optionValueId = clickedSwatch.dataset.optionValueId || '';
      const connectedProductUrl = clickedSwatch.dataset.connectedProductUrl || '';
      const requestUrl = this.buildRequestUrl(clickedSwatch, 'product-card', [optionValueId]);

      this.fetchUpdatedSection(requestUrl, {
        detail: { optionValueId, variantId: firstAvailableVariantId, connectedProductUrl },
      });
      return;
    }

    // For all other cases, use the default behavior
    super.variantChanged(event);
  }

  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}
